/**
 * Shared building blocks for the Reports module.
 * Single file to keep import surface small.
 */
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar as CalendarIcon, Download, Printer, FileText, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { formatCurrency } from '../../lib/formatters';

/** Single KPI tile used on every report page. */
export const KPICard = ({ label, value, hint, accent = 'default', testId, icon: Icon }) => {
    const accentMap = {
        default: { bg: 'bg-[#1A1A1A]/5', text: 'text-[#1A1A1A]', icon: 'text-[#1A1A1A]' },
        success: { bg: 'bg-[#2A9D8F]/10', text: 'text-[#2A9D8F]', icon: 'text-[#2A9D8F]' },
        warning: { bg: 'bg-[#E9C46A]/15', text: 'text-[#8a6d00]', icon: 'text-[#8a6d00]' },
        danger: { bg: 'bg-[#E76F51]/10', text: 'text-[#E76F51]', icon: 'text-[#E76F51]' },
        accent: { bg: 'bg-[#C08478]/10', text: 'text-[#C08478]', icon: 'text-[#C08478]' },
        info: { bg: 'bg-[#264653]/10', text: 'text-[#264653]', icon: 'text-[#264653]' },
    };
    const a = accentMap[accent] || accentMap.default;
    return (
        <Card className="border-0 shadow-sm" data-testid={testId}>
            <CardContent className="p-4 flex items-start gap-3">
                {Icon && (
                    <div className={`w-9 h-9 rounded-lg ${a.bg} flex items-center justify-center shrink-0`}>
                        <Icon size={18} className={a.icon} />
                    </div>
                )}
                <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-[1.5px] text-gray-500 font-semibold">{label}</p>
                    <p className={`text-xl font-bold ${a.text} truncate`}>{value}</p>
                    {hint != null && <p className="text-[10px] text-gray-400 mt-0.5">{hint}</p>}
                </div>
            </CardContent>
        </Card>
    );
};

/** Localized currency formatter used by every report. */
export const useCurrency = () => {
    const { i18n } = useTranslation();
    return (v) => formatCurrency(v, i18n.language);
};

/** Date range picker pair (start + end) + 3 presets — sticky filter bar. */
export const DateRangeBar = ({ start, end, onChange, onPreset, children, extraTestId = 'date-range' }) => {
    const { t } = useTranslation(['entities', 'common']);
    return (
        <Card className="border-0 shadow-sm sticky top-0 z-10 no-print" data-testid={`${extraTestId}-bar`}>
            <CardContent className="p-3 flex flex-wrap items-center gap-3">
                <div className="space-y-1">
                    <label className="text-[10px] uppercase tracking-[1.5px] text-gray-500 font-semibold block">
                        {t('entities:reports.start_date')}
                    </label>
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant="outline" data-testid="start-date-picker" className="w-44 justify-start text-left font-normal h-9">
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {start ? format(start, 'dd MMM yyyy') : t('entities:reports.select_date')}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                            <Calendar mode="single" selected={start} onSelect={(d) => onChange({ start: d, end })} initialFocus />
                        </PopoverContent>
                    </Popover>
                </div>
                <div className="space-y-1">
                    <label className="text-[10px] uppercase tracking-[1.5px] text-gray-500 font-semibold block">
                        {t('entities:reports.end_date')}
                    </label>
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant="outline" data-testid="end-date-picker" className="w-44 justify-start text-left font-normal h-9">
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {end ? format(end, 'dd MMM yyyy') : t('entities:reports.select_date')}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                            <Calendar mode="single" selected={end} onSelect={(d) => onChange({ start, end: d })} initialFocus />
                        </PopoverContent>
                    </Popover>
                </div>
                <div className="flex gap-2 items-end">
                    <Button variant="outline" size="sm" onClick={() => onPreset(0)} data-testid="preset-today" className="h-9">
                        {t('entities:reports.presets.today')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => onPreset(7)} data-testid="preset-7" className="h-9">
                        {t('entities:reports.presets.last_7')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => onPreset(30)} data-testid="preset-30" className="h-9">
                        {t('entities:reports.presets.last_30')}
                    </Button>
                </div>
                {children}
            </CardContent>
        </Card>
    );
};

/** Export bar (CSV + Print/PDF). */
export const ExportBar = ({ onCsv, onPrint, disabled = false }) => {
    const { t } = useTranslation(['entities', 'common']);
    return (
        <div className="flex gap-3 no-print" data-testid="export-bar">
            <Button variant="outline" size="sm" onClick={onCsv} disabled={disabled} data-testid="export-csv-btn" className="rounded-full">
                <Download size={14} className="mr-2" />
                {t('common:actions.export_csv')}
            </Button>
            <Button variant="outline" size="sm" onClick={onPrint} disabled={disabled} data-testid="export-print-btn" className="rounded-full">
                <Printer size={14} className="mr-2" />
                {t('entities:reports.print_pdf')}
            </Button>
        </div>
    );
};

/** Loading / Empty / Error states. */
export const ReportLoader = () => (
    <div className="py-20 flex justify-center" data-testid="report-loading">
        <Loader2 size={36} className="animate-spin text-[#C08478]" />
    </div>
);

export const ReportEmpty = ({ icon: Icon = FileText, message }) => (
    <div className="flex flex-col items-center justify-center py-16 text-gray-400" data-testid="report-empty">
        <Icon size={48} className="mb-3 opacity-50" />
        <p className="text-sm">{message}</p>
    </div>
);

export const ReportError = ({ message, onRetry }) => {
    const { t } = useTranslation();
    return (
        <div className="flex flex-col items-center justify-center py-12 text-[#E76F51]" data-testid="report-error">
            <p className="text-sm mb-3">{message || t('common:toasts.load_failed')}</p>
            {onRetry && (
                <Button variant="outline" size="sm" onClick={onRetry}>
                    {t('common:actions.next', { defaultValue: 'Retry' })}
                </Button>
            )}
        </div>
    );
};

/**
 * Convert array-of-objects to CSV and trigger a download.
 * Excel-friendly: UTF-8 BOM, quoted strings.
 */
export const downloadCsv = (filename, columns, rows) => {
    const escape = (v) => {
        if (v == null) return '';
        const s = String(v).replace(/"/g, '""');
        return /[",\n;]/.test(s) ? `"${s}"` : s;
    };
    const header = columns.map((c) => escape(c.label)).join(',');
    const body = rows.map((r) => columns.map((c) => escape(typeof c.value === 'function' ? c.value(r) : r[c.key])).join(',')).join('\n');
    const csv = '\uFEFF' + header + '\n' + body;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};
